import { supabaseManager } from '@/services/supabaseManager';
import { OrganizationService } from '@/services/organizations/organizationService';
import { simpleUsersService } from '@/services/simpleUsers/simpleUsersService';
import { useSavedAccountsStore } from '@/stores/savedAccounts/savedAccountsStore';
import type { AuthStoreGet, AuthStoreSet } from './auth.types';
import { invalidateOrganizationQueries } from './auth.operatorActions';
import { useOrganizationStore } from '../organizationStore';

export const INVALID_SIMPLE_USER_PIN_MESSAGE = 'INVALID_SIMPLE_USER_PIN';

/**
 * Member and simple-user sign-in, including fast-switch resume for members.
 */
export const createSignInActions = (set: AuthStoreSet, get: AuthStoreGet) => ({
  signIn: async (
    email: string,
    password: string,
    organizationSlug: string,
    loading: boolean = true,
  ): Promise<{ error: Error | null }> => {
    const previousOrganizationSlug = supabaseManager.getCurrentOrganizationSlug();

    try {
      set({ loading: loading });
      await supabaseManager.connectToOrganizationBySlug(organizationSlug);
      const client = supabaseManager.getCurrentClient();
      const { data, error } = await client.auth.signInWithPassword({ email, password });

      if (error) {
        if (previousOrganizationSlug && previousOrganizationSlug !== organizationSlug) {
          await supabaseManager.connectToOrganizationBySlug(previousOrganizationSlug);
        }
        return { error };
      }

      if (data.user && data.session) {
        let organization;
        try {
          organization = await OrganizationService.getOrganizationBySlug(organizationSlug);
        } catch {
          await client.auth.signOut();
          if (previousOrganizationSlug && previousOrganizationSlug !== organizationSlug) {
            await supabaseManager.connectToOrganizationBySlug(previousOrganizationSlug);
          }
          return { error: new Error('Invalid login credentials') };
        }

        set({
          user: data.user,
          session: data.session,
          isAuthenticated: true,
          isInWaitingScreen: false,
        });
        get().applyMemberOperator();
        useOrganizationStore.getState().setCurrentOrganizationSlug(organizationSlug);

        const { notifyOrganizationChange } = await import('../authStore');
        notifyOrganizationChange();

        await get().fetchUserProfile(data.user.id);

        useSavedAccountsStore.getState().upsertAccount({
          email,
          slug: organizationSlug,
          orgName: organization.name || organizationSlug,
        });

        invalidateOrganizationQueries(organizationSlug);
      }

      return { error: null };
    } catch (error) {
      if (previousOrganizationSlug && previousOrganizationSlug !== organizationSlug) {
        await supabaseManager.connectToOrganizationBySlug(previousOrganizationSlug);
      }
      return { error };
    } finally {
      set({ loading: false });
    }
  },

  signInSimpleUser: async (
    simpleUserId: string,
    pin: string,
    organizationSlug: string,
  ): Promise<{ error: Error | null }> => {
    try {
      set({ loading: true });

      await supabaseManager.connectToOrganizationBySlug(organizationSlug);
      const client = supabaseManager.getCurrentClient();

      const {
        data: { session },
      } = await client.auth.getSession();
      if (!session?.user) {
        return { error: new Error('No member session; sign in as a member first') };
      }

      const simpleUser = await simpleUsersService.loginSimpleUser(simpleUserId, pin);
      if (!simpleUser) {
        return { error: new Error(INVALID_SIMPLE_USER_PIN_MESSAGE) };
      }

      const {
        data: { session: refreshed },
      } = await client.auth.getSession();
      const activeSession = refreshed ?? session;
      const activeUser = activeSession?.user ?? session.user;

      set({
        user: activeUser,
        session: activeSession,
        isAuthenticated: true,
        isInWaitingScreen: false,
      });
      get().applySimpleUserOperator(simpleUser);

      localStorage.setItem('currentOrganizationSlug', organizationSlug);

      const { notifyOrganizationChange } = await import('../authStore');
      notifyOrganizationChange();

      const { useOrganizationStore } = await import('../organizationStore');
      useOrganizationStore.getState().setCurrentOrganizationSlug(organizationSlug);

      invalidateOrganizationQueries(organizationSlug);

      return { error: null };
    } catch (error) {
      return { error };
    } finally {
      set({ loading: false });
    }
  },
});
